// USDA FoodData Central client wrapper.
//
// Calls our /api/usda-search Pages Function (which proxies USDA + handles
// CORS) and normalizes hits into the same SearchResult shape that the OFF
// flow uses, so AddFoodPicker can render USDA rows in the same component.
//
// This is the second source after OFF. Wired as a parallel search in the
// picker (text query → both call concurrently; results render in
// separate sections). Barcode fallback also calls this when OFF returns
// nothing for a scanned UPC.

import type { FoodItem, MealEstimate } from "../types";
import type { CanonicalUnit } from "../portions";
import { guessFoodCategory } from "../portions";
import type { SearchResult } from "./openfoodfacts";
import { apiFetch } from "../apiFetch";
import { cacheGet, cacheSet, DAY_MS } from "../cache";

interface UsdaRawHit {
  source: "usda";
  fdcId: number;
  name: string;
  brand?: string;
  barcode?: string;
  portion: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g?: number;
  grams_per_portion?: number;
}

interface UsdaResponse {
  results?: UsdaRawHit[];
  error?: string;
}

/** Text search via USDA. Returns SearchResult[] for direct render parity
 *  with the existing OFF results in AddFoodPicker. Cached for 24h per
 *  query to keep proxy calls cheap. */
export async function searchUsdaFoods(query: string, limit = 5): Promise<SearchResult[]> {
  const q = query.trim();
  if (!q || q.length < 2) return [];

  const cacheKey = `usda.search.${q.toLowerCase()}.${limit}`;
  const cached = cacheGet<SearchResult[]>(cacheKey);
  if (cached) return cached;

  let data: UsdaResponse;
  try {
    const resp = await apiFetch(`/api/usda-search?q=${encodeURIComponent(q)}`);
    if (!resp.ok) {
      // 429/502 = USDA rate-limited or upstream failure. Degrade quietly —
      // OFF is still returning results, the user doesn't need to know.
      return [];
    }
    data = (await resp.json()) as UsdaResponse;
  } catch {
    return [];
  }

  const hits = (data.results ?? []).slice(0, limit);
  const results = hits.map(usdaHitToSearchResult);
  cacheSet(cacheKey, results, DAY_MS);
  return results;
}

/** Barcode lookup via USDA. Returns the first matching SearchResult or null.
 *  Called as a fallback when OFF returns nothing for a scanned UPC. */
export async function lookupUsdaBarcode(barcode: string): Promise<SearchResult | null> {
  const upc = barcode.trim();
  if (!upc) return null;

  const cacheKey = `usda.upc.${upc}`;
  const cached = cacheGet<SearchResult | null>(cacheKey);
  if (cached !== null) return cached;

  let data: UsdaResponse;
  try {
    const resp = await apiFetch(`/api/usda-search?upc=${encodeURIComponent(upc)}`);
    if (!resp.ok) return null;
    data = (await resp.json()) as UsdaResponse;
  } catch {
    return null;
  }

  const hit = (data.results ?? [])[0];
  if (!hit) {
    // Cache the null so a missing barcode doesn't re-hit USDA on every
    // re-scan in the same session.
    cacheSet(cacheKey, null, DAY_MS);
    return null;
  }
  const result = usdaHitToSearchResult(hit);
  cacheSet(cacheKey, result, DAY_MS);
  return result;
}

/** Convert the proxy's normalized USDA shape into the SearchResult that the
 *  AddFoodPicker + quickLogSearchResult flow expects. */
function usdaHitToSearchResult(hit: UsdaRawHit): SearchResult {
  const item: FoodItem = {
    name: hit.name,
    portion: hit.portion,
    calories: hit.calories,
    protein_g: hit.protein_g,
    carbs_g: hit.carbs_g,
    fat_g: hit.fat_g,
    fiber_g: hit.fiber_g,
    grams_per_portion: hit.grams_per_portion,
    food_category: guessFoodCategory(hit.name)
  };
  const estimate: MealEstimate = {
    items: [item],
    totals: {
      calories: hit.calories,
      protein_g: hit.protein_g,
      carbs_g: hit.carbs_g,
      fat_g: hit.fat_g,
      fiber_g: hit.fiber_g ?? 0
    },
    confidence: "high",
    clarifying_questions: []
  };
  return {
    code: hit.barcode ?? `usda-${hit.fdcId}`,
    name: hit.name,
    brand: hit.brand,
    imageUrl: undefined,
    estimate,
    gramWeight: hit.grams_per_portion
  };
}

// Re-export the unit type so callers don't need to dive into portions.ts
// just to handle USDA results. Defensive against tree-shaking.
export type { CanonicalUnit };
